/// <reference lib="dom" />
import { useEvent } from '@shared-core/hooks/use-event'
import { type ComponentRef, type RefObject, useEffect } from 'react'
import type { ScrollView } from 'react-native'

export function useCardCarouselMouse(
  carouselRef: RefObject<ComponentRef<typeof ScrollView> | null>,
  step: number,
  onSettled: (index: number) => void,
) {
  const notifySettled = useEvent(onSettled)
  // Мышь прокручивает карусель напрямую; touch и трекпад остаются браузерными.
  useEffect(() => {
    const node: unknown = carouselRef.current?.getScrollableNode()

    if (!(node instanceof HTMLElement)) return

    let pointerId: number | null = null
    let startX = 0
    let startOffset = 0
    let dragged = false
    let suppressClick = false
    let previousSnap = ''
    let previousBehavior = ''
    let locked = false
    let frame: number | null = null
    let lastX = 0
    let lastTime = 0
    let velocity = 0
    const unlock = () => {
      if (!locked) return
      node.style.scrollSnapType = previousSnap
      node.style.scrollBehavior = previousBehavior
      locked = false
    }
    const lock = () => {
      if (locked) return
      previousSnap = node.style.scrollSnapType
      previousBehavior = node.style.scrollBehavior
      node.style.scrollSnapType = 'none'
      node.style.scrollBehavior = 'auto'
      locked = true
    }
    const down = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || pointerId !== null) return

      if (frame !== null) window.cancelAnimationFrame(frame)
      frame = null
      pointerId = event.pointerId
      lastX = event.clientX
      lastTime = event.timeStamp
      velocity = 0
      startX = event.clientX
      startOffset = node.scrollLeft
      dragged = false
      suppressClick = false
    }
    const move = (event: PointerEvent) => {
      if (event.pointerId !== pointerId) return
      const delta = event.clientX - startX
      const elapsed = event.timeStamp - lastTime

      if (elapsed > 0) velocity = velocity * 0.25 + ((lastX - event.clientX) / elapsed) * 0.75
      lastX = event.clientX
      lastTime = event.timeStamp

      if (!dragged && Math.abs(delta) < 6) return

      if (!dragged) {
        dragged = true
        const focused = document.activeElement

        if (focused instanceof HTMLElement && node.contains(focused)) focused.blur()
        lock()
      }
      event.preventDefault()
      node.scrollLeft = startOffset - delta
    }
    const up = (event: PointerEvent) => {
      if (event.pointerId !== pointerId) return
      pointerId = null

      if (!dragged) {
        unlock()
        return
      }
      suppressClick = true
      dragged = false
      const maxOffset = Math.max(0, node.scrollWidth - node.clientWidth)
      const from = node.scrollLeft
      const releaseVelocity = event.type === 'pointercancel' || event.timeStamp - lastTime > 100 ? 0 : velocity
      const projected = Math.max(0, Math.min(from + releaseVelocity * 220, maxOffset))
      let targetIndex = Math.round(projected / step)

      if (Math.abs(releaseVelocity) > 0.25) {
        const startIndex = Math.round(startOffset / step)
        targetIndex =
          releaseVelocity > 0 ? Math.max(startIndex + 1, targetIndex) : Math.min(startIndex - 1, targetIndex)
      }
      const target = Math.max(0, Math.min(targetIndex * step, maxOffset))
      const startedAt = window.performance.now()
      const duration = Math.max(180, Math.min(420, Math.abs(target - from) / Math.max(0.8, Math.abs(releaseVelocity))))

      // До конца инерции держим CSS snap выключенным, иначе браузер возвращает карту назад.
      const tick = (now: number) => {
        const progress = Math.min(1, (now - startedAt) / duration)
        node.scrollLeft = from + (target - from) * (1 - (1 - progress) ** 3)

        if (progress < 1) {
          frame = window.requestAnimationFrame(tick)
        } else {
          frame = null
          node.scrollLeft = target
          unlock()
          notifySettled(Math.round(target / step))
        }
      }
      frame = window.requestAnimationFrame(tick)
    }

    const click = (event: MouseEvent) => {
      if (!suppressClick) return
      suppressClick = false
      event.preventDefault()
      event.stopImmediatePropagation()
    }
    const dragStart = (event: DragEvent) => event.preventDefault()
    node.addEventListener('pointerdown', down)
    node.addEventListener('click', click, true)
    node.addEventListener('dragstart', dragStart)
    window.addEventListener('pointermove', move, { passive: false })
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame)
      unlock()
      node.removeEventListener('pointerdown', down)
      node.removeEventListener('click', click, true)
      node.removeEventListener('dragstart', dragStart)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
    }
  }, [carouselRef, step, notifySettled])
}
